import { Component, forwardRef, Input, OnInit, isDevMode, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PershingTranslationService } from '@pershing/pershing-core';
import { PershingCommonLabels } from '../i18n/pershing-input-label';
import { startWith, map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'pershing-input',
  templateUrl: 'pershing-input.component.html',
  styles: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PershingInputComponent),
      multi: true
    }
  ]

})

export class PershingInputComponent implements OnInit, ControlValueAccessor {
      @Input() autocomplete = 'off';
      @Input() autofocus;
      @Input() checked;
      @Input() max;
        @Input() class;
      @Input() maxlength;
      @Input() min;
      @Input() name;
      @Input() placeholder;
      @Input() required;
      @Input() type = 'text';
      @Input() value = '';

     @Input() formControl: FormControl;
     @Input() formControlMetadata: any;
     @Input() showTooltip: boolean = false;
     @Input() enableFilterDropdown: boolean = false;
     @Input() filterOptions: any = [];

     @Output() blur: EventEmitter<any> = new EventEmitter();

  formControlErrorMessage: string;
  formControlWarningMessage: string;
  formControlErrorState: boolean = false;
  formControlWarningState: boolean = false;
  reactOnlyAfterBlur: boolean = false;

  tooltipContent: string;
  tooltipClass: string;
  matFormFieldClass: string = 'input-success';

  filteredOptions: Observable<string[]>;

  constructor(private translationService: PershingTranslationService, private cd: ChangeDetectorRef) {
    this.translationService.addTranslationFolder('@pershing/pershing-common', 'node_modules/@pershing/pershing-common/lib');
  }

  ngOnInit() {

    this.filteredOptions = this.formControl.valueChanges
      .pipe(startWith(''),
        map(value => this._filter(value))
    );

    this.formControl.statusChanges.subscribe ( (status) => {
      if (this.formControl.touched) {
        if (status === 'INVALID') {
          this.checkFormStatus();
        } else {
          this.resetState();
        }
      }
    });

  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.filterOptions.filter(option => option.toLowerCase().includes(filterValue));
  }

  invokeValidation() {
    this.reactOnlyAfterBlur = true;
    this.checkFormStatus();
    this.blur.emit();
  }
  checkFormStatus() {
    this.resetState();
    if (this.reactOnlyAfterBlur) {
          let errors = this.formControl.errors;
          let formControlMetadata = this.formControlMetadata;
          let consolidatedError = '';
          let consolidatedWarning = '';
          let tooltipContent = '';
          let tooltipState = '';
          if (errors) {
              Object.keys(errors).forEach( (errorKey) => {
                if (formControlMetadata[errorKey]) {
                        if (formControlMetadata[errorKey].errorType === 'error') {
                          let translation = this.translationService.get(formControlMetadata[errorKey].message);
                          translation.subscribe((value) => {
                            if (consolidatedError === '') {
                              consolidatedError = '&nbsp;' + consolidatedError + value;
                            } else {
                              consolidatedError = consolidatedError + '<br>' + value;
                            }
                          });
                        }
                        if (formControlMetadata[errorKey].errorType  === 'warning') {
                          let translation = this.translationService.get(formControlMetadata[errorKey].message);
                          translation.subscribe((value) => {
                            if (consolidatedWarning === '') {
                              consolidatedWarning = '&nbsp;' + consolidatedWarning + value;
                            } else {
                              consolidatedWarning = consolidatedWarning + '<br>' + value;
                            }
                          });
                        }
                } else {
                  // Metadata not available
                  if (errorKey === 'required') {
                      this.translationService.get(PershingCommonLabels.REQUIRED_ERROR_CASE_MESSAGE).subscribe(translatedLiteral => {
                        consolidatedError = translatedLiteral;
                      });
                  } else {
                      // could not determine the input state as metadata not available
                      this.translationService.get(PershingCommonLabels.DEFAULT_ERROR_CASE_MESSAGE).subscribe(translatedLiteral => {
                        consolidatedError = translatedLiteral;
                      });
                  }
                }
              });
          }
          // Give priority to error messages and ignore other messages (say : warning messages will be ignored)
          // If multiple messages of same type (say multiple error messages), they will be grouped and shown one below the another
          if (consolidatedError && consolidatedError !== '') {
            this.formControlErrorMessage = consolidatedError;
            this.formControlErrorState = true;
            this.detectChange();
            tooltipContent = this.formatTooltipContent(consolidatedError);
            tooltipState = 'error-state';
          } else if (consolidatedWarning && consolidatedWarning !== '') {
            this.formControlWarningMessage = consolidatedWarning;
            this.formControlWarningState = true;
            this.detectChange();
            tooltipContent = this.formatTooltipContent(consolidatedWarning);
            tooltipState = 'warning-state';
          }
          // set tooltip content and class only when tooltip is enabled
          if (this.showTooltip) {
            this.tooltipContent = tooltipContent;
            this.tooltipClass = tooltipState;
          }
          this.updateFormFieldStyling();
    }
  }
  resetState() {
    this.formControlErrorMessage = '';
    this.formControlWarningMessage = '';
    this.formControlErrorState = false;
    this.formControlWarningState = false;
    this.tooltipContent = '';
    this.tooltipClass = '';
    this.updateFormFieldStyling();
  }

  detectChange() {
    if (isDevMode()) {
      this.cd.detectChanges();
    }
  }

  formatTooltipContent(message: string) {
    // remove any html content in the tooltip message
    let regex = /&nbsp;|<br>/gi;
    message = message.replace(regex, ' ');
    return message;
  }

  updateFormFieldStyling() {
    if (this.showTooltip) {
      if (this.formControlWarningState && !this.formControlErrorState) {
        // this.matFormFieldClass = ['tooltip', 'input-warning'];
        this.matFormFieldClass = 'input-warning'; // use 'tooltip-input-warning' once tooltip class added in branding
      } else {
        // this.matFormFieldClass = ['tooltip', 'input-success'];
        this.matFormFieldClass = 'input-success'; // use 'tooltip-input-success' once tooltip class added in branding
      }
    } else {
      if (this.formControlWarningState && !this.formControlErrorState) {
        this.matFormFieldClass = 'input-warning';
      } else {
        this.matFormFieldClass = 'input-success';
      }
    }
  }

  onChange = (value: string) => {};
  onTouched = () => {};

  writeValue(value: string): void {
    // this.control.setValue(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

}
